/**
 * @fileoverview `EventModuleSection` (WEB-707) — the section chrome every
 * Event v2 module-tier renderer wraps its body in.
 *
 * This is the file the whole module tier imports. It is deliberately free of
 * category-specific logic: no slug checks. WEB-719 wires the `hasContent`
 * flip here — one place, so no individual module implements a degrade note.
 * When `hasContent` is false the body is `ModuleDegradeNote` (lexicon copy
 * looked up by kind, not slug) and the meta reads whatever
 * `getEventModuleNotPublishedMeta` returns for that kind — "Not listed" for
 * most, and nothing at all for the kinds that opt out (WEB-851); heading and
 * ordinal still render so the page's shape does not change per event.
 *
 * ## Chrome
 *
 * - Ordinal in `catInk` mono (`'01'`, `'02'`, … — assigned by
 *   `resolveEventModules` after gating).
 * - `<h2>` at `--evt-sec` (WEB-702 size ladder).
 * - Right-aligned meta: published `module.meta` when `hasContent` is true
 *   (omitted when that string is null); the kind's
 *   `getEventModuleNotPublishedMeta` string ("Not listed", or null for a kind
 *   that opts out) when it is false.
 * - Top hairline (`1px var(--border)`) and `--evt-sgap` padding-top.
 * - "More" link when `module.moreHref` is a non-empty URL, including on a
 *   degraded module (WEB-805: if we tell the reader to check the ticket
 *   page, the source CTA must still be reachable). A null/blank href omits
 *   the control entirely — never a dead `#` link.
 *
 * Chroma is caller-supplied (the same hexes `getCategoryChroma` already
 * resolved for the page). This primitive does not call the resolver.
 *
 * ## WEB-750 — chrome type scale corrected against the measured design
 *
 * WEB-707 shipped before the Event v2 design source (the `.dc.html` Design
 * Canvas mockup) was reachable, so it built the ordinal/heading/meta/"more"
 * link to ticket prose rather than measured values. WEB-750
 * (`docs/design-system/event-v2-m2-module-specs.md` §0.3/§0.4) corrected all
 * four against real `getComputedStyle` reads:
 *
 * - **Ordinal**: 16px → **11px**; tracking 0.08em → **0.16em**.
 * - **`<h2>`**: weight 700 → **600**; `leading-tight` (1.25) → **1.45**;
 *   added `letter-spacing: -0.01em` (previously unset).
 * - **Section meta**: 16px → **11px**; tracking → `var(--ls-caps)` (0.08em,
 *   already an existing token); added `uppercase`; color `--fg-2` → **`--fg-3`**.
 * - **"More" link**: mono → **`--font-display`** (Space Grotesk); 16px →
 *   **13px**; added `font-semibold` (600); color `--fg-2` → **`var(--accent)`**
 *   (the shipped-token equivalent of the design's `--teal`); `margin-top`
 *   `--evt-mgap` (20px, laddered) → **`var(--sp-4)`** (16px, fixed — this
 *   value does not ladder per spec §7); added a trailing `→` in its own
 *   `aria-hidden` `<span>`.
 *
 * **Deliberately NOT reusing `EVT_MICRO_LABEL_CLASS`** (WEB-736,
 * `lib/eventV2MicroLabel.ts`). The mockup uses two distinct 11px tracking
 * values here (ordinal 0.16em, meta 0.08em) and neither is the M1
 * micro-label's 0.14em — see spec §0.3's explicit warning against
 * "harmonising" these two scales.
 *
 * Also corrected, from spec §0.2 (not in the issue's condensed deviation
 * table, but present in the authoritative spec section this ticket names):
 * header row gap 16px (`gap-4`) → **14px** (`gap-[14px]`), plus `flex-wrap`
 * (was missing); and header→body spacing, previously the section-rhythm
 * `--evt-sgap` (40px) on the body wrapper, corrected to the header's own
 * `margin-bottom: var(--sp-4)` (16px) per §0.2's measured value.
 */

import type { ReactElement, ReactNode } from 'react';

import { TextIcon } from '@/components/atoms/TextIcon';
import { cn } from '@/components/atoms/primitives/utils';
import { getEventModuleDegradeCopy, getEventModuleNotPublishedMeta } from '@/config/eventLexicon';
import type { ResolvedEventModule } from '@/lib/eventDetail/modules';

import { ModuleDegradeNote } from './ModuleDegradeNote';
import type { ModuleRowChroma } from './ModuleRowList';

/** Fallback label when `moreHref` is set but `moreLabel` is null. */
const MORE_LABEL_FALLBACK = 'More';

/** 44px — the WCAG 2.2 AA minimum touch target the "more" link honours. */
const MORE_LINK_MIN_SIZE_CLASS = 'min-h-11 min-w-11';

export interface EventModuleSectionProps {
  /** Already-gated module from `resolveEventModules`. */
  module: ResolvedEventModule;
  /** Caller-resolved category chroma. Never derived in this file. */
  chroma: ModuleRowChroma;
  /**
   * Lineup only: when the event has a usable ticket link, the degrade note
   * adds the ticket-page sentence. Other kinds ignore it.
   */
  hasTicketLink?: boolean;
  /** Module body — typically a `ModuleRowList`. Ignored when `hasContent` is false. */
  children?: ReactNode;
  /** Optional additional class names on the `<section>`. */
  className?: string;
}

/**
 * `EventModuleSection` — pure-props section chrome. Server-safe: no hooks,
 * no `'use client'`. The "more" link is a plain `<a>` so this file never
 * takes a Next.js routing dependency the rest of the module tier doesn't
 * need.
 */
export function EventModuleSection({
  module,
  chroma,
  hasTicketLink = false,
  children,
  className,
}: EventModuleSectionProps): ReactElement {
  const publishedMeta =
    typeof module.meta === 'string' && module.meta.trim().length > 0 ? module.meta.trim() : null;
  // WEB-851 — the degraded meta is a per-kind lexicon lookup, not a single
  // constant: `seatTiers` resolves null and paints no meta at all. Kept as a
  // lookup rather than an `if (kind === …)` here so this file stays free of
  // kind-specific opinions, exactly as the degrade copy below already is.
  const meta = module.hasContent ? publishedMeta : getEventModuleNotPublishedMeta(module.kind);
  const trimmedMoreHref = typeof module.moreHref === 'string' ? module.moreHref.trim() : '';
  const moreHref = trimmedMoreHref.length > 0 && trimmedMoreHref !== '#' ? trimmedMoreHref : null;
  const moreLabel =
    typeof module.moreLabel === 'string' && module.moreLabel.trim().length > 0
      ? module.moreLabel.trim()
      : MORE_LABEL_FALLBACK;

  return (
    <section
      className={cn(
        'min-w-0 overflow-x-hidden border-t border-ds-border pt-(--evt-sgap)',
        className,
      )}
      data-testid="event-module-section"
      data-module-kind={module.kind}
      data-module-ordinal={module.ordinal}
    >
      <header
        className="mb-4 flex flex-wrap items-baseline justify-between gap-[14px]"
        data-testid="event-module-section-header"
      >
        <div className="flex min-w-0 items-baseline gap-3">
          <span
            data-testid="event-module-section-ordinal"
            // 11px / 0.16em (spec §0.3) — NOT `--ls-eyebrow` (0.14em, WEB-736's
            // M1 micro-label scale). Weight is intentionally undeclared: the
            // design leaves it at the inherited 400.
            className="shrink-0 text-micro tracking-[0.16em] font-mono"
            style={{ color: chroma.catInk }}
          >
            {module.ordinal}
          </span>
          <h2
            data-testid="event-module-section-title"
            className="m-0 min-w-0 wrap-break-word font-semibold tracking-[-0.01em] leading-[1.45] text-fg-1 font-display text-(length:--evt-sec)"
          >
            {module.title}
          </h2>
        </div>
        {meta !== null ? (
          <p
            data-testid="event-module-section-meta"
            // 11px / var(--ls-caps) (0.08em) / uppercase / --fg-3 (spec §0.3).
            // --ls-caps is a pre-existing token; this is the ONE of the two
            // 11px tracking values in this section that matches an M1 token —
            // the ordinal above (0.16em) does not.
            className="m-0 shrink-0 text-right text-micro tracking-caps text-fg-3 font-mono"
          >
            {meta}
          </p>
        ) : null}
      </header>

      {module.hasContent ? (
        children ? (
          <div className="min-w-0" data-testid="event-module-section-body">
            {children}
          </div>
        ) : null
      ) : (
        <div className="min-w-0" data-testid="event-module-section-body">
          <ModuleDegradeNote
            copy={getEventModuleDegradeCopy(module.kind, {
              hasTicketLink: module.kind === 'lineup' && hasTicketLink,
            })}
          />
        </div>
      )}

      {moreHref !== null ? (
        <a
          href={moreHref}
          data-testid="event-module-section-more"
          className={cn(
            'mt-4 inline-flex items-center gap-2',
            MORE_LINK_MIN_SIZE_CLASS,
            // Space Grotesk 13px/600, spec §0.4. `--accent` is the shipped
            // token equivalent of the design's `--teal` (light #0E7B7B / dark
            // #4FC4C4 — byte-identical to `--loci-color-primary[-light]`).
            // The trailing `!` beat globals.css's then-unlayered `a { color:
            // inherit }` reset (now layered in styles/base.css, WEB-853; same
            // gotcha documented in decisionStyles.ts).
            'text-small font-semibold text-ds-accent! font-display',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg-1',
          )}
        >
          {moreLabel}
          {/* Trailing glyph as its own span (spec §0.4) — aria-hidden so a
              screen reader doesn't announce a bare "right arrow" after the
              label. */}
          <TextIcon />
        </a>
      ) : null}
    </section>
  );
}
